(() => {
  if (customElements.get('wat-ai-product-search')) return;
  class AiProductSearch extends HTMLElement {
    connectedCallback() {
      if (this.initialized) { this.suspended = false; this.watchPageLifecycle(); this.watchAnimation(); return; }
      this.initialized = true;
      this.suspended = false;
      this.watchPageLifecycle();
      this.form = this.querySelector('form');
      this.input = this.querySelector('input');
      this.status = this.querySelector('[role="status"]');
      this.resultCount = this.querySelector('[data-ai-result-count]');
      this.shell = this.querySelector('.wat-ai-result-shell');
      this.carousel = this.querySelector('.wat-ai-carousel');
      this.list = this.querySelector('.wat-ai-product-grid, .swiper-wrapper');
      this.carousel.classList.remove('swiper', 'swiper-visible');
      this.list.classList.remove('swiper-wrapper');
      this.list.classList.add('wat-ai-product-grid');
      this.list.setAttribute('role', 'list');
      this.dialog = this.querySelector('.wat-ai-modal');
      this.launch = this.querySelector('.wat-ai-search-launch');
      if (this.dialog && this.launch) {
        this.dialog.setAttribute('data-lenis-prevent', '');
        this.querySelector('.wat-ai-modal-card')?.setAttribute('data-lenis-prevent', '');
        this.launch.addEventListener('click', () => this.openSearch());
        this.querySelector('.wat-ai-modal-close').addEventListener('click', () => this.dialog.close());
        this.dialog.addEventListener('keydown', event => { if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.dialog.close(); } });
        this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); });
        this.dialog.addEventListener('close', () => {
          clearTimeout(this.modalTimer); this.dialog.classList.remove('wat-ai-search-open');
          clearTimeout(this.timer); this.controller?.abort(); ++this.sequence;
          this.removeAttribute('aria-busy');
          this.resetResults(); this.status.textContent = '';
          this.unlockViewport();
          document.documentElement.style.overflow = this.savedOverflow || '';
          this.pausedLenis?.start?.(); this.pausedLenis = null;
          this.returnFocus?.setAttribute('aria-expanded', 'false');
          if (!this.suspended && this.isConnected) (this.returnFocus || this.launch).focus({ preventScroll: true });
          this.startTyping();
        });
        this.watchAnimation();
      }
      this.sequence = 0;
      this.composing = false;
      this.form.addEventListener('submit', (event) => { event.preventDefault(); clearTimeout(this.timer); this.search(); });
      this.input.addEventListener('input', () => { if (!this.composing) this.schedule(); });
      this.input.addEventListener('compositionstart', () => { this.composing = true; clearTimeout(this.timer); this.controller?.abort(); ++this.sequence; });
      this.input.addEventListener('compositionend', () => { this.composing = false; this.schedule(); });
      this.querySelectorAll('[data-example]').forEach((button) => button.addEventListener('click', () => {
        this.input.value = button.dataset.example;
        this.input.focus({ preventScroll: true });
        clearTimeout(this.timer);
        this.search();
      }));
    }
    openSearch(trigger = this.launch) {
      if (!this.dialog || this.dialog.open) return;
      this.returnFocus = trigger;
      trigger?.setAttribute('aria-expanded', 'true');
      clearTimeout(this.typingTimer);
      this.classList.remove('wat-ai-animation-active');
      this.savedOverflow = document.documentElement.style.overflow;
      this.pausedLenis = window.lenis && !window.lenis.isStopped ? window.lenis : null;
      this.pausedLenis?.stop?.();
      document.documentElement.style.overflow = 'hidden';
      this.lockViewport();
      this.dialog.showModal();
      this.syncViewport();
      clearTimeout(this.modalTimer);
      this.modalTimer = setTimeout(() => {
        this.dialog.classList.add('wat-ai-search-open');
        this.input.focus({ preventScroll: true });
      }, matchMedia('(max-width: 767px), (prefers-reduced-motion: reduce)').matches ? 0 : 500);
      if (this.input.value.trim().length >= 3) this.schedule();
    }
    lockViewport() {
      if (this.bodyLock) return;
      const body = document.body;
      const properties = ['position', 'top', 'left', 'right', 'width', 'overflow'];
      this.bodyLock = { x: window.scrollX, y: window.scrollY,
        styles: properties.map(key => [key, body.style.getPropertyValue(key), body.style.getPropertyPriority(key)]) };
      body.style.position = 'fixed'; body.style.top = `-${this.bodyLock.y}px`;
      body.style.left = '0'; body.style.right = '0'; body.style.width = '100%'; body.style.overflow = 'hidden';
      this.viewportHandler ||= () => this.syncViewport();
      window.visualViewport?.addEventListener('resize', this.viewportHandler);
      window.visualViewport?.addEventListener('scroll', this.viewportHandler);
      window.addEventListener('resize', this.viewportHandler);
    }
    syncViewport() {
      if (!this.dialog) return;
      const viewport = window.visualViewport;
      this.dialog.style.setProperty('--wat-ai-viewport-height', `${viewport?.height || window.innerHeight}px`);
      this.dialog.style.setProperty('--wat-ai-viewport-top', `${viewport?.offsetTop || 0}px`);
      // Safari may scroll the dialog itself when opening the keyboard. Only the result card scrolls.
      this.dialog.scrollTop = 0;
      const inner = this.querySelector('.wat-ai-modal-inner');
      if (inner) inner.scrollTop = 0;
    }
    unlockViewport() {
      window.visualViewport?.removeEventListener('resize', this.viewportHandler);
      window.visualViewport?.removeEventListener('scroll', this.viewportHandler);
      window.removeEventListener('resize', this.viewportHandler);
      if (!this.bodyLock) return;
      const saved = this.bodyLock; this.bodyLock = null;
      saved.styles.forEach(([key, value, priority]) => {
        if (value) document.body.style.setProperty(key, value, priority);
        else document.body.style.removeProperty(key);
      });
      const scrollBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(saved.x, saved.y);
      document.documentElement.style.scrollBehavior = scrollBehavior;
    }
    watchPageLifecycle() {
      this.pageHideHandler ||= () => this.pauseRuntime();
      this.pageShowHandler ||= () => {
        if (!this.isConnected || !this.suspended) return;
        this.suspended = false; this.watchAnimation();
      };
      window.addEventListener('pagehide', this.pageHideHandler);
      window.addEventListener('pageshow', this.pageShowHandler);
    }
    disconnectedCallback() {
      this.pauseRuntime();
      window.removeEventListener('pagehide', this.pageHideHandler);
      window.removeEventListener('pageshow', this.pageShowHandler);
    }
    pauseRuntime() {
      this.suspended = true;
      clearTimeout(this.typingTimer); clearTimeout(this.modalTimer); clearTimeout(this.timer);
      this.animationObserver?.disconnect(); this.animationObserver = null;
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.motionPreference?.removeEventListener?.('change', this.visibilityHandler);
      this.classList.remove('wat-ai-animation-active');
      if (this.dialog?.open) this.dialog.close();
      clearTimeout(this.typingTimer); this.controller?.abort(); ++this.sequence;
      this.resetResults();
    }
    watchAnimation() {
      if (!this.launch || this.animationObserver) return;
      this.animationVisible = false;
      this.motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
      this.visibilityHandler ||= () => {
        this.classList.toggle('wat-ai-page-hidden', document.hidden);
        this.startTyping();
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
      this.motionPreference.addEventListener?.('change', this.visibilityHandler);
      if (typeof IntersectionObserver === 'function') {
        this.animationObserver = new IntersectionObserver(entries => {
          this.animationVisible = entries.some(entry => entry.isIntersecting);
          this.startTyping();
        }, { threshold: 0.1 });
        this.animationObserver.observe(this.launch);
      } else {
        this.animationVisible = true;
        this.animationObserver = { disconnect() {} };
      }
      this.visibilityHandler();
    }
    startTyping() {
      clearTimeout(this.typingTimer); this.typingTimer = null;
      this.classList.remove('wat-ai-animation-active');
      const target = this.querySelector('.wat-ai-typed');
      if (!target || this.suspended || !this.isConnected || this.dialog?.open || document.hidden || !this.animationVisible || this.dataset.animateExamples === 'false' || this.motionPreference?.matches) return;
      const phrases = [...this.querySelectorAll('[data-example]')].map(el => el.dataset.example).filter(Boolean);
      if (!phrases.length) return;
      // Reuse a single text node: do not recreate DOM nodes for every character.
      if (!this.typingState) {
        const text = document.createTextNode(target.textContent);
        target.replaceChildren(text);
        this.typingState = { index: 0, length: 0, deleting: false, text };
      }
      this.classList.add('wat-ai-animation-active');
      const tick = () => {
        this.typingTimer = null;
        if (this.suspended || !this.isConnected || this.dialog?.open || document.hidden || !this.animationVisible) { this.classList.remove('wat-ai-animation-active'); return; }
        const state = this.typingState;
        const phrase = phrases[state.index % phrases.length];
        state.length = Math.max(0, Math.min(phrase.length, state.length + (state.deleting ? -1 : 1)));
        state.text.data = phrase.slice(0, state.length);
        let delay = state.deleting ? 60 : 100;
        if (state.length === phrase.length && !state.deleting) { state.deleting = true; delay = 1800; }
        else if (state.length === 0 && state.deleting) { state.deleting = false; state.index = (state.index + 1) % phrases.length; delay = 400; }
        this.typingTimer = setTimeout(tick, delay);
      };
      this.typingTimer = setTimeout(tick, 500);
    }
    showLoading() {
      if (this.list.firstElementChild?.classList.contains('wat-ai-skeleton')) { this.shell.hidden = false; return; }
      const placeholders = Array.from({ length: 6 }, () => {
        const card = document.createElement('div');
        card.className = 'wat-ai-product-grid-item wat-ai-skeleton placeholder-glow';
        card.setAttribute('aria-hidden', 'true');
        card.innerHTML = '<span class="wat-ai-skeleton-image placeholder col-12"></span><div class="wat-ai-skeleton-detail"><span class="wat-ai-skeleton-title placeholder col-10"></span><span class="wat-ai-skeleton-title wat-ai-skeleton-short placeholder col-7"></span><span class="wat-ai-skeleton-price placeholder col-6"></span></div>' ;
        return card;
      });
      this.list.replaceChildren(...placeholders); this.shell.hidden = false;
    }
    clearResultCount() {
      if (this.resultCount) { this.resultCount.hidden = true; this.resultCount.textContent = ''; }
    }
    resetResults() {
      this.clearResultCount();
      this.slider?.destroy?.(true, true); this.slider = null;
      this.carousel.classList.remove('wat-ai-carousel-fallback');
      this.shell.hidden = true; this.list.replaceChildren();
    }
    schedule() {
      this.clearResultCount();
      clearTimeout(this.timer); this.controller?.abort(); ++this.sequence;
      this.removeAttribute('aria-busy');
      const query = this.input.value.trim();
      if (query.length < 3) { this.resetResults(); this.status.textContent = query ? 'Bitte mindestens drei Zeichen eingeben.' : ''; return; }
      this.status.textContent = 'Wir suchen gleich passende Motive …';
      this.showLoading();
      this.timer = setTimeout(() => this.search(), Number(this.dataset.delay) || 1000);
    }
    async search() {
      this.clearResultCount();
      if (this.composing) return;
      const query = this.input.value.trim();
      if (query.length < 3) return;
      let endpoint;
      try { endpoint = new URL(this.dataset.endpoint); if (endpoint.protocol !== 'https:') throw new Error(); }
      catch { this.resetResults(); this.status.textContent = 'Die Motivsuche ist noch nicht eingerichtet.'; return; }
      this.controller?.abort();
      const controller = this.controller = new AbortController();
      const sequence = ++this.sequence;
      const timeout = setTimeout(() => controller.abort(), 55000);
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Wir suchen passende Motive für dich …';
      this.showLoading();
      try {
        const response = await fetch(endpoint, { method: 'POST', credentials: 'omit', signal: controller.signal,
          headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ query }) });
        const data = await response.json();
        if (!response.ok) {
          const reference = [data.code, data.request_id].filter(Boolean).join(' / ');
          throw new Error(`${data.error || 'Die Suche ist gerade nicht verfügbar.'}${reference ? ` (${reference})` : ''}`);
        }
        if (!Array.isArray(data.products)) throw new Error('Die Suche ist gerade nicht verfügbar.');
        const root = new URL(this.dataset.root || '/', location.origin);
        const limit = 15; // Keep the requested result depth even when an older section setting is smaller.
        const candidates = data.products.slice(0, limit).filter((item) => /^[a-zA-Z0-9_-]+$/.test(item.handle));
        const cards = [];
        for (let offset = 0; offset < candidates.length; offset += 6) {
          const batch = await Promise.all(candidates.slice(offset, offset + 6).map(async (item) => {
            try {
              const url = new URL(`products/${encodeURIComponent(item.handle)}`, root);
              url.searchParams.set('section_id', 'ai-search-product-card');
              const res = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
              if (!res.ok) return null;
              const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
              const rendered = doc.querySelector('[data-ai-product-card]');
              if (!rendered || rendered.dataset.available !== 'true' || rendered.dataset.handle !== item.handle) return null;
              const slide = document.createElement('div');
              slide.className = 'featured-product-img block-radius wat-ai-product-grid-item';
              slide.setAttribute('role', 'listitem');
              slide.dataset.item = 'true'; slide.append(...rendered.childNodes);
              return slide;
            } catch (error) { if (controller.signal.aborted) throw error; return null; }
          }));
          cards.push(...batch.filter(Boolean));
        }
        if (sequence !== this.sequence) return;
        if (!cards.length) { this.resetResults(); this.status.textContent = data.restricted ? 'Für diese Anfrage zeigen wir keine Motive.' : 'Dazu haben wir keine verfügbaren Produkte gefunden. Versuche einen anderen Motivbegriff.'; return; }
        this.list.replaceChildren(...cards); this.shell.hidden = false;
        const countTarget = this.resultCount || this.status;
        this.status.textContent = '';
        countTarget.textContent = `${cards.length} ${cards.length === 1 ? 'passendes Produkt' : 'passende Produkte'} gefunden.`;
        if (this.resultCount) this.resultCount.hidden = false;
        if (typeof data.notice === 'string' && data.notice) this.status.textContent = data.notice;
        else if (data.fallback) this.status.textContent = 'Keine genauen Motivtreffer – hier sind Empfehlungen passend zu deiner Auswahl.';
        const names = { price: 'Preis', size: 'Größe', color: 'Farbe', material: 'Material' };
        const unsupported = (data.unsupported_filters || []).map((key) => names[key]).filter(Boolean);
        if (unsupported.length) this.status.textContent += ` ${unsupported.join(', ')} wurden bei dieser Suche nicht gefiltert.`;
        this.querySelectorAll('.wat-ai-carousel-controls').forEach(el => { el.hidden = true; });
      } catch (error) {
        if (sequence !== this.sequence) return;
        this.resetResults();
        this.status.textContent = controller.signal.aborted ? 'Die Suche dauert gerade zu lange. Bitte versuche es erneut.'
          : error instanceof TypeError ? 'Der Suchserver ist nicht erreichbar oder der Zugriff aus dem Shop wird blockiert. Bitte versuche es später erneut.'
          : error.message || 'Die Suche ist gerade nicht verfügbar.';
      } finally { clearTimeout(timeout); if (sequence === this.sequence) this.removeAttribute('aria-busy'); }
    }

  }
  customElements.define('wat-ai-product-search', AiProductSearch);
})();
